import React from "react";
import MessageContent from "./MessageContent";
import { Bot, User } from "lucide-react";

export default function ChatMessages({
  messages,
  isLoading,
  error,
  messagesEndRef,
  deepThinking,
}) {
  const [thoughtSec, setThoughtSec] = React.useState(0);
  const [showThought, setShowThought] = React.useState(false);
  // Track when a bot message (not temp) is added
  React.useEffect(() => {
    if (!deepThinking) {
      setShowThought(false);
      return;
    }
    const lastBotMsg = [...messages]
      .reverse()
      .find((m) => m.type === "bot" && !m.temp);
    const lastTempMsg = [...messages].reverse().find((m) => m.temp);
    if (lastBotMsg && lastTempMsg) {
      const tempTime = lastTempMsg.id;
      const botTime = lastBotMsg.id;
      const sec = Math.max(
        1,
        Math.round((parseInt(botTime) - parseInt(tempTime)) / 1000)
      );
      setThoughtSec(sec);
      setShowThought(true);
    }
  }, [messages, deepThinking]);

  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, messagesEndRef]);

  return (
    <div className="flex-1 overflow-y-scroll no-scrollbar p-4 space-y-4 relative h-full">
      {messages.map((message, idx) => {
        // Only show 'Thinking...' if message.temp is true and not showThought
        if (message.temp && !showThought) {
          return (
            <div
              key={message.id}
              className="flex items-center gap-2 text-green-700 font-semibold"
            >
              <Bot className="w-5 h-5 animate-pulse" />
              <span>🧠 Thinking...</span>
            </div>
          );
        }
        // Show 'Thought for X sec' before the bot message
        if (
          showThought &&
          deepThinking &&
          message.type === "bot" &&
          !message.temp
        ) {
          return (
            <React.Fragment key={message.id + "-thought"}>
              <div className="flex items-center gap-2 text-blue-700 font-semibold">
                <Bot className="w-5 h-5" />
                <span>🧠 Thinking...</span>
              </div>
              <div
                className={`flex items-start gap-3 ${
                  message.type === "user" ? "justify-end" : ""
                }
                }`}
              >
                {message.type === "bot" && (
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-blue-900" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    message.type === "user"
                      ? "bg-[#003366] text-white"
                      : "bg-white border border-gray-200"
                  }
                  }`}
                >
                  <MessageContent
                    content={message.content}
                    type={message.type}
                  />
                </div>
                {message.type === "user" && (
                  <div className="w-8 h-8 rounded-full bg-[#003366] flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        }
        // Default message rendering
        return (
          <div
            key={message.id}
            className={`flex items-start gap-3 ${
              message.type === "user" ? "justify-end" : ""
            }`}
          >
            {message.type === "bot" && (
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Bot className="w-5 h-5 text-blue-900" />
              </div>
            )}
            <div
              className={`max-w-[80%] p-3 rounded-lg ${
                message.type === "user"
                  ? "bg-[#003366] text-white"
                  : "bg-white border border-gray-200"
              }`}
            >
              <MessageContent content={message.content} type={message.type} />
            </div>
            {message.type === "user" && (
              <div className="w-8 h-8 rounded-full bg-[#003366] flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
        );
      })}

      {error && (
        <div className="text-red-500 p-3 bg-red-50 rounded-lg">
          Error: {error}
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}
